import { useSelector } from "react-redux";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowUp } from "@styled-icons/feather/ArrowUp";
import { Plus } from "@styled-icons/feather/Plus";

import * as styles from "./styles";
import { useAppState } from "contextApi/appContext";
import { useComposerActions, useComposerState } from "contextApi/composerContext";
import {
  ComposerAgentEventName,
  ComposerAgentProfile,
  fetchComposerAgentProfiles,
  fetchComposerAgentSession,
  isRefusal,
  recordComposerAgentEvent,
  saveComposerAgentSession,
  stepComposerAgent
} from "services/composerAgent";
import { useComposerTools } from "./useComposerTools";
import { useSmoothText } from "./useSmoothText";
import { isSupportedChatFile, uploadChatFile } from "./chatUploads";
import { ComposerIssue, deriveComposerIssues, diffChannels, formatComposerEvent, shouldCheckProfile } from "./composerIssues";
import { canReturnFocusToChat } from "./focus";
import { buildGreeting, splitGreeting } from "./greeting";
import { ReplyMarkdown } from "./ReplyMarkdown";
import { WillowMark } from "./WillowMark";
import { ProfileMenu } from "./ProfileMenu";
import { TaggedPeople } from "./TaggedPeople";
import { getPostKey, listRecentActions, mergeSessionLog, SessionLogEntry } from "./sessionLog";
import { MessageOrigin, OptionPick, messageSentProperties, toolResultProperties } from "./chatEvents";
import {
  AgentMessage,
  AgentPart,
  AgentStepResponse,
  AgentToolResultPart,
  ComposerSnapshot,
  SnapshotChannel,
  channelLabel,
  couldBeSilentReply,
  describeToolCall,
  describeToolCallInProgress,
  emptiedTurnIndexes,
  eventMessage,
  findRecoveredFailures,
  getAttachments,
  getEventText,
  hiddenTurnIndexes,
  isOpeningMessage,
  layoutAssistantRows,
  openingMessage,
  selectedChannelIds,
  getText,
  getToolCalls,
  indexToolResults,
  splitReplyOptions,
  streamedReplyText,
  stripStaleThinking,
  toolResultMessage,
  userMessage
} from "./protocol";

// One user message may trigger several tool rounds; this bounds a runaway loop.
const MAX_STEPS_PER_TURN = 8;
const ACCEPTED_FILES = "image/png,image/jpeg,image/gif,image/webp,.pdf,.ppt,.pptx,.doc,.docx,.txt,.md,.csv";
// A composer problem is raised only once it has held still this long, so typing or a quick channel toggle costs no turn.
const EVENT_DEBOUNCE_MS = 2500;
// A channel change is mentioned in the event only when it happened shortly before the problem appeared.
const CHANNEL_CHANGE_WINDOW_MS = 10_000;

const starterPrompts = (snapshot: ComposerSnapshot): string[] => [
  "Make it shorter",
  "Suggest three hooks",
  ...(snapshot.attachments.count === 0 ? ["Generate an image", "Find a stock photo"] : []),
  "Schedule for the best time tomorrow"
];
const TEXTAREA_MAX_HEIGHT_PX = 140;

const channelLabelsOf = (ids: string[], channels: SnapshotChannel[]): string[] =>
  ids.flatMap(id => {
    const channel = channels.find(candidate => candidate.id === id);
    return channel ? [channelLabel(channel)] : [];
  });

const fitTextarea = (element: HTMLTextAreaElement) => {
  element.style.height = "auto";
  element.style.height = `${Math.min(element.scrollHeight, TEXTAREA_MAX_HEIGHT_PX)}px`;
};

interface ChatUpload {
  id: string;
  name: string;
  status: "uploading" | "ready" | "failed";
  part?: AgentPart;
  error?: string;
}

interface ChannelChange {
  added: string[]; // channel ids
  removed: string[];
  at: number;
}

const Thinking: React.FC<{ label?: string }> = ({ label = "Thinking…" }) => <styles.Thinking>{label}</styles.Thinking>;

const AgentAvatar: React.FC<{ thinking?: boolean }> = ({ thinking }) => (
  <styles.Avatar $thinking={thinking}>
    <WillowMark />
  </styles.Avatar>
);

// A row that continues the turn above keeps the avatar column empty, so one turn reads as one message.
const AvatarColumn: React.FC<{ continued: boolean; thinking?: boolean }> = ({ continued, thinking }) =>
  continued ? <styles.AvatarSpacer /> : <AgentAvatar thinking={thinking} />;

type ActionStatus = "pending" | "done" | "error";

interface AssistantTurnProps {
  message: AgentMessage;
  results: Record<string, AgentToolResultPart>;
  hiddenCallIds: Set<string>; // failures the next message recovered from
  channels: ComposerSnapshot["channels"];
  profiles: ComposerAgentProfile[];
  continued: boolean;
  working: boolean; // this row heads the turn still in progress, so its avatar shows the thinking state
  animate: boolean; // only turns added after the panel mounted slide in
  onPickOption?: (option: string, pick: OptionPick) => void; // set only for the latest reply while the chat is idle
}

const AssistantTurn: React.FC<AssistantTurnProps> = ({ message, results, hiddenCallIds, channels, profiles, continued, working, animate, onPickOption }) => {
  const { text, options } = splitReplyOptions(getText(message.content));
  const toolCalls = getToolCalls(message.content).filter(call => !hiddenCallIds.has(call.id));

  return (
    <styles.AssistantTurn $animate={animate} $continued={continued}>
      <AvatarColumn continued={continued} thinking={working} />
      <styles.AssistantMessage>
        {!!text && <ReplyMarkdown text={text} />}
        {toolCalls.length > 0 && (
          <styles.Activity>
            {toolCalls.map(call => {
              const result = results[call.id];
              const status: ActionStatus = !result ? "pending" : result.isError ? "error" : "done";
              return (
                <React.Fragment key={call.id}>
                  <styles.ActionRow $status={status}>
                    <styles.ActionIcon $status={status}>
                      {status === "pending" ? <styles.Spinner /> : <i className={status === "error" ? "icon-error-circle" : "icon-check"} />}
                    </styles.ActionIcon>
                    <span>{result?.isError ? result.output : status === "pending" ? describeToolCallInProgress(call, channels, profiles) : describeToolCall(call, channels, profiles)}</span>
                  </styles.ActionRow>
                  {!!result?.tagged?.length && <TaggedPeople people={result.tagged} />}
                </React.Fragment>
              );
            })}
          </styles.Activity>
        )}
        {!!onPickOption && options.length > 0 && (
          <styles.Chips>
            {options.map((option, index) => (
              <styles.Chip key={option} type="button" onClick={() => onPickOption(option, { position: index + 1, count: options.length })}>
                {option}
              </styles.Chip>
            ))}
          </styles.Chips>
        )}
      </styles.AssistantMessage>
    </styles.AssistantTurn>
  );
};

const UserTurn: React.FC<{ message: AgentMessage; animate: boolean }> = ({ message, animate }) => {
  if (isOpeningMessage(message.content)) return null;
  const eventText = getEventText(message.content);
  if (eventText !== null) {
    return (
      <styles.EventLine $animate={animate}>
        <span>{eventText}</span>
      </styles.EventLine>
    );
  }

  const attachments = getAttachments(message.content);
  return (
    <styles.UserTurn $animate={animate}>
      <styles.UserBubble>
        {getText(message.content)}
        {attachments.length > 0 && (
          <styles.MessageFiles>
            {attachments.map(attachment => (
              <styles.MessageFile key={`${attachment.kind}-${attachment.name}`}>
                {attachment.kind === "image" ? "Image" : "File"} · {attachment.name}
              </styles.MessageFile>
            ))}
          </styles.MessageFiles>
        )}
      </styles.UserBubble>
    </styles.UserTurn>
  );
};

interface StreamingTurnProps {
  text: string; // the reply so far, as the model sends it
  threadRef: React.RefObject<HTMLDivElement>;
  revealRef: React.MutableRefObject<() => Promise<void>>; // lets the turn loop wait for the reveal to catch up before committing
  holdBack: boolean; // answering a quiet event: nothing shows while the reply could still be the silent one
  continued: boolean;
}

const StreamingTurn: React.FC<StreamingTurnProps> = ({ text, threadRef, revealRef, holdBack, continued }) => {
  const { shown, waitUntilRevealed } = useSmoothText(text);
  revealRef.current = waitUntilRevealed;
  const revealed = streamedReplyText(shown);

  useEffect(() => {
    const thread = threadRef.current;
    if (thread) thread.scrollTop = thread.scrollHeight;
  }, [revealed, threadRef]);

  if (holdBack && couldBeSilentReply(revealed)) return null;
  return (
    <styles.AssistantTurn $animate $continued={continued}>
      <AvatarColumn continued={continued} thinking />
      <styles.AssistantMessage>
        {revealed ? <styles.AssistantText>{revealed}</styles.AssistantText> : <Thinking />}
      </styles.AssistantMessage>
    </styles.AssistantTurn>
  );
};

const CreateAI: React.FC = () => {
  const accountId = useSelector((state: any) => state.account.data.id);
  const { getSnapshot, executeToolCall, restoreVisibleCaption, registerUploadedImage } = useComposerTools();
  const agentChat = useComposerState(state => state.agentChat);
  const { history, previousLog, profileId, profileChosenBy, profiles, isReplying } = agentChat;
  const isCreateEnabled = useAppState(state => state.showContentFactoryTab) === true;
  const profileTouchedAt = useComposerState(state => state.agentTouch.profile);
  const postData = useComposerState(state => state.postData);
  const setAgentChat = useComposerActions(actions => actions.setAgentChat);
  const getCloseSignal = useComposerActions(actions => actions.getCloseSignal);

  const [draft, setDraft] = useState("");
  const [uploads, setUploads] = useState<ChatUpload[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamedText, setStreamedText] = useState("");
  const [liveResults, setLiveResults] = useState<Record<string, AgentToolResultPart>>({});
  const chatRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<AgentMessage[]>(history);
  const previousLogRef = useRef<SessionLogEntry[] | null>(previousLog);
  const agentChatRef = useRef(agentChat);
  const createEnabledRef = useRef(isCreateEnabled);
  const busyRef = useRef(false);
  const issuesRef = useRef<ComposerIssue[]>([]);
  const notifiedIssueIds = useRef(new Set<string>());
  const previousSelectedChannels = useRef<string[] | null>(null);
  const channelChange = useRef<ChannelChange | null>(null);
  const checkedChannelIds = useRef<string[] | null>(null); // the selection the assistant last saw, so a later change can ask it to recheck the profile
  const greetingSeed = useRef(Math.random());
  const revealRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const initialHistoryLength = useRef(history.length);
  const chatIdRef = useRef(agentChat.chatId ?? window.crypto.randomUUID());

  const snapshot = getSnapshot();
  const postKey = getPostKey(postData);
  const isLoadingPrevious = !!postKey && previousLog === null;
  previousLogRef.current = previousLog;
  agentChatRef.current = agentChat;
  // A reply that outlived an earlier mount of this chat commits only to state.
  if (!busyRef.current) historyRef.current = history;
  createEnabledRef.current = isCreateEnabled;
  const greeting = useMemo(
    () => buildGreeting(getSnapshot(), greetingSeed.current, listRecentActions(previousLog ?? [])),
    [getSnapshot, previousLog]
  );
  const { headline: greetingHeadline, body: greetingBody } = splitGreeting(greeting);
  const channels = snapshot.channels;
  const results = useMemo(() => ({ ...indexToolResults(history), ...liveResults }), [history, liveResults]);
  const recoveredFailures = useMemo(() => findRecoveredFailures(history, results), [history, results]);
  const hiddenTurns = useMemo(
    () => new Set([...hiddenTurnIndexes(history), ...emptiedTurnIndexes(history, recoveredFailures)]),
    [history, recoveredFailures]
  );
  const lastVisibleIndex = history.reduce((last, _message, index) => (hiddenTurns.has(index) ? last : index), -1);
  const activeProfile = profiles.find(profile => profile.id === profileId) ?? null;
  const showProfileLine = !!activeProfile;
  const rows = useMemo(() => layoutAssistantRows(history, hiddenTurns, showProfileLine), [history, hiddenTurns, showProfileLine]);
  const isQuietCheck = hiddenTurns.has(history.length - 1);
  const isWorking = isReplying && !isQuietCheck;
  const readyParts = uploads.filter(upload => upload.status === "ready").map(upload => upload.part as AgentPart);
  const isUploading = uploads.some(upload => upload.status === "uploading");
  const canSend = !isReplying && !isUploading && (!!draft.trim() || readyParts.length > 0);

  const hasReply = history.some(message => message.role === "assistant");
  const conversationStarted = history.some(message => message.role === "user" && !isOpeningMessage(message.content));

  const issues = deriveComposerIssues(snapshot);
  issuesRef.current = issues;
  const issueKey = issues.map(issue => issue.id).join("\n");
  const selectedChannelsKey = JSON.stringify(selectedChannelIds(channels));
  const captionLength = Object.values(snapshot.captions).join("").length;

  useEffect(() => {
    const thread = threadRef.current;
    if (thread) thread.scrollTop = thread.scrollHeight;
  }, [history, isReplying, streamedText, uploads]);

  // Coming back to the tab mid-conversation: the problems on screen were already discussed.
  useEffect(() => {
    if (historyRef.current.length > 0) issuesRef.current.forEach(issue => notifiedIssueIds.current.add(issue.id));
  }, []);

  // A Create post starts on the profile it was written with; a choice made earlier in this composer survives tab switches.
  useEffect(() => {
    if (!isCreateEnabled) return;
    let cancelled = false;
    fetchComposerAgentProfiles(accountId, postKey ?? undefined)
      .then(({ profiles: loaded, createProfileId }) => {
        if (cancelled) return;
        const current = agentChatRef.current.profileId;
        const keepsCurrent = !!current && loaded.some(profile => profile.id === current);
        setAgentChat(
          keepsCurrent
            ? { profiles: loaded }
            : { profiles: loaded, profileId: createProfileId ?? loaded[0]?.id ?? null, profileChosenBy: createProfileId ? "create" : null }
        );
      })
      .catch(err => console.error("Failed to load the Create AI profiles", { accountId, postKey, err }));
    return () => {
      cancelled = true;
    };
  }, [accountId, postKey, setAgentChat, isCreateEnabled]);

  useEffect(() => {
    if (!postKey || previousLog !== null) return;
    let cancelled = false;
    fetchComposerAgentSession(accountId, postKey)
      .then(session => {
        if (!cancelled) setAgentChat({ previousLog: session.log });
      })
      .catch(err => {
        console.error("Failed to load the Create AI session", { accountId, postKey, err });
        if (!cancelled) setAgentChat({ previousLog: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, postKey, previousLog, setAgentChat]);

  const trackChatEvent = useCallback(
    (name: ComposerAgentEventName, properties: Record<string, unknown> = {}) => {
      recordComposerAgentEvent(accountId, { chatId: chatIdRef.current, postKey: postKey ?? undefined, name, properties }).catch(err =>
        console.error("Failed to record the Create AI event", { accountId, name, err })
      );
    },
    [accountId, postKey]
  );

  // The chat remounts on every tab switch, but its id lives in the composer state, so only a new chat counts as shown.
  useEffect(() => {
    if (agentChatRef.current.chatId) return;
    setAgentChat({ chatId: chatIdRef.current });
    trackChatEvent("chat_shown", { isNewPost: !postKey, createEnabled: createEnabledRef.current });
  }, [postKey, setAgentChat, trackChatEvent]);

  const commitHistory = useCallback(
    (messages: AgentMessage[], closeSignal?: AbortSignal) => {
      historyRef.current = messages;
      // A turn can outlive its composer: the state then belongs to the next post, and historyRef still feeds this post's saved log.
      if (!closeSignal?.aborted) setAgentChat({ history: messages });
    },
    [setAgentChat]
  );

  const updateUpload = useCallback((id: string, changes: Partial<ChatUpload>) => {
    setUploads(current => current.map(upload => (upload.id === id ? { ...upload, ...changes } : upload)));
  }, []);

  const addFiles = useCallback(
    (files: ArrayLike<File>) => {
      Array.from(files).forEach(file => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        if (!isSupportedChatFile(file)) {
          setUploads(current => [...current, { id, name: file.name, status: "failed", error: "unsupported file type" }]);
          return;
        }
        setUploads(current => [...current, { id, name: file.name, status: "uploading" }]);
        uploadChatFile(accountId, file)
          .then(part => {
            if (part.type === "image_ref") registerUploadedImage(part);
            updateUpload(id, { status: "ready", part });
          })
          .catch(err => {
            console.error("Create AI upload failed", { name: file.name, err });
            updateUpload(id, { status: "failed", error: "upload failed" });
          });
      });
    },
    [accountId, registerUploadedImage, updateUpload]
  );

  const removeUpload = (id: string) => setUploads(current => current.filter(upload => upload.id !== id));

  const runTurn = useCallback(
    async (message: AgentMessage) => {
      if (busyRef.current || agentChatRef.current.isReplying) return;
      busyRef.current = true;
      setError(null);
      setAgentChat({ isReplying: true });
      const closeSignal = getCloseSignal();
      const viewBefore = getSnapshot();
      let lastSeenSnapshot = viewBefore;
      let messages = [...historyRef.current, message];
      commitHistory(messages, closeSignal);

      try {
        for (let step = 0; step < MAX_STEPS_PER_TURN; step++) {
          // The stream ignores a signal that aborted before it started, so a closed composer is checked here.
          if (closeSignal?.aborted) break;
          setStreamedText("");
          lastSeenSnapshot = getSnapshot();
          // Read through the ref: set_profile switches the profile between steps of this same turn.
          const { profileId: stepProfileId, profileChosenBy: stepChooser } = agentChatRef.current;
          const response: AgentStepResponse = await stepComposerAgent(
            accountId,
            {
              messages: stripStaleThinking(messages),
              snapshot: lastSeenSnapshot,
              previousSession: previousLogRef.current ?? undefined,
              postKey: postKey ?? undefined,
              profileId: stepProfileId ?? undefined,
              profileChosenBy: stepChooser ?? undefined,
              createEnabled: createEnabledRef.current,
              chatId: chatIdRef.current
            },
            { onText: text => setStreamedText(current => current + text), signal: closeSignal }
          );
          await revealRef.current();
          messages = [...messages, { role: "assistant", content: response.content }];
          commitHistory(messages, closeSignal);
          setStreamedText("");

          const toolCalls = getToolCalls(response.content);
          if (response.stopReason !== "tool_call" || toolCalls.length === 0) break;

          const toolResults: AgentToolResultPart[] = [];
          for (const call of toolCalls) {
            const startedAtMillis = Date.now();
            const result = await executeToolCall(call, closeSignal);
            trackChatEvent("tool_result", toolResultProperties({ tool: call.name, result, durationMs: Date.now() - startedAtMillis }));
            toolResults.push(result);
            setLiveResults(current => ({ ...current, [call.id]: result }));
          }
          messages = [...messages, toolResultMessage(toolResults)];
          commitHistory(messages, closeSignal);
          setLiveResults({});
        }
      } catch (err) {
        if (!closeSignal?.aborted) {
          console.error("Create AI turn failed", { err });
          const refused = isRefusal(err);
          setError(refused ? "Create AI isn't available for you right now." : "Something went wrong. Try again.");
          trackChatEvent("error_shown", { kind: refused ? "refused" : "failed" });
        }
      } finally {
        if (!closeSignal?.aborted) restoreVisibleCaption(viewBefore);
        // Problems the agent's own tools caused are already in its tool results; raising them as events would loop.
        deriveComposerIssues(getSnapshot()).forEach(issue => notifiedIssueIds.current.add(issue.id));
        checkedChannelIds.current = selectedChannelIds(lastSeenSnapshot.channels);
        if (postKey) {
          const names = { channels: getSnapshot().channels, profiles: agentChatRef.current.profiles };
          saveComposerAgentSession(accountId, postKey, mergeSessionLog(previousLogRef.current ?? [], historyRef.current, names)).catch(err =>
            console.error("Failed to save the Create AI session", { accountId, postKey, err })
          );
        }
        setStreamedText("");
        setLiveResults({});
        busyRef.current = false;
        if (!closeSignal?.aborted) setAgentChat({ isReplying: false });
        if (getEventText(message.content) === null && canReturnFocusToChat(chatRef.current, document.activeElement)) inputRef.current?.focus();
      }
    },
    [accountId, commitHistory, executeToolCall, getCloseSignal, getSnapshot, postKey, restoreVisibleCaption, setAgentChat, trackChatEvent]
  );

  const regreetForProfileId = useRef<string | null>(null);

  const pickProfile = useCallback(
    (pickedId: string) => {
      setAgentChat({ profileId: pickedId, profileChosenBy: "user" });
      if (!conversationStarted) regreetForProfileId.current = pickedId;
    },
    [conversationStarted, setAgentChat]
  );

  // Runs after the render that stored the pick, so the opening step already reads the new profile.
  useEffect(() => {
    if (!regreetForProfileId.current || regreetForProfileId.current !== profileId || busyRef.current) return;
    regreetForProfileId.current = null;
    commitHistory([]);
    runTurn(openingMessage());
  }, [profileId, commitHistory, runTurn]);

  const send = useCallback(
    (text: string, origin: MessageOrigin = { source: "typed" }) => {
      const trimmed = text.trim();
      // The user can type ahead while a reply is running; Enter then keeps the draft instead of dropping it.
      if (busyRef.current || agentChatRef.current.isReplying || isUploading || (!trimmed && readyParts.length === 0)) return;
      setDraft("");
      setUploads([]);
      if (inputRef.current) inputRef.current.style.height = "auto";
      trackChatEvent("message_sent", messageSentProperties({ text: trimmed, origin, hasAttachment: readyParts.length > 0 }));
      runTurn(userMessage(trimmed || "See the attached file.", readyParts));
    },
    [isUploading, readyParts, runTurn, trackChatEvent]
  );

  useEffect(() => {
    const selected: string[] = JSON.parse(selectedChannelsKey);
    const before = previousSelectedChannels.current;
    previousSelectedChannels.current = selected;
    if (checkedChannelIds.current === null) checkedChannelIds.current = selected;
    if (!before) return;
    const { added, removed } = diffChannels(before, selected);
    if (added.length || removed.length) channelChange.current = { added, removed, at: Date.now() };
  }, [selectedChannelsKey]);

  // The greeting is the model's: once the previous session is known, an opening turn asks it to greet and offer the options that fit.
  useEffect(() => {
    if (isLoadingPrevious || historyRef.current.length > 0 || busyRef.current) return;
    runTurn(openingMessage());
  }, [isLoadingPrevious, runTurn]);

  useEffect(() => {
    if (isReplying || isLoadingPrevious) return;
    const currentIds = new Set(issuesRef.current.map(issue => issue.id));
    notifiedIssueIds.current.forEach(id => {
      if (!currentIds.has(id)) notifiedIssueIds.current.delete(id);
    });
    const pending = issuesRef.current.filter(issue => !notifiedIssueIds.current.has(issue.id));
    const selected: string[] = JSON.parse(selectedChannelsKey);
    const checked = checkedChannelIds.current;
    const profileCheckDue = shouldCheckProfile({
      profileCount: profiles.length,
      profileChosenBy,
      hasCaption: captionLength > 0,
      checkedChannelIds: checked,
      selectedChannelIds: selected
    });
    if (pending.length === 0 && !profileCheckDue) return;

    const timer = setTimeout(() => {
      pending.forEach(issue => notifiedIssueIds.current.add(issue.id));
      const recent = channelChange.current;
      channelChange.current = null;
      const isRecentChange = !!recent && Date.now() - recent.at < CHANNEL_CHANGE_WINDOW_MS;
      const change = profileCheckDue && checked ? diffChannels(checked, selected) : isRecentChange && recent ? recent : { added: [], removed: [] };
      const accountChannels = getSnapshot().channels;
      runTurn(
        eventMessage(
          formatComposerEvent({
            addedChannels: channelLabelsOf(change.added, accountChannels),
            removedChannels: channelLabelsOf(change.removed, accountChannels),
            issues: pending
          }),
          { quiet: pending.length === 0 }
        )
      );
    }, EVENT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [issueKey, captionLength, isReplying, isLoadingPrevious, runTurn, selectedChannelsKey, profiles.length, profileChosenBy, getSnapshot]);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    send(draft);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send(draft);
    }
  };

  const onDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (!isDragging) setIsDragging(true);
  };

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    addFiles(event.dataTransfer.files);
  };

  return (
    <styles.Chat ref={chatRef} onDragOver={onDragOver} onDragLeave={() => setIsDragging(false)} onDrop={onDrop}>
      <styles.Thread ref={threadRef} $compact={!conversationStarted}>
        {showProfileLine && (
          <styles.AssistantTurn>
            <AgentAvatar thinking={(isWorking && rows.trailingHead === "lead") || (!hasReply && !isReplying && !error)} />
            <styles.AssistantMessage>
              <styles.ProfileLine>
                Writing as{" "}
                <ProfileMenu
                  variant="inline"
                  profiles={profiles}
                  activeId={profileId}
                  onPick={pickProfile}
                  disabled={isReplying}
                  touchedAt={profileTouchedAt}
                />
              </styles.ProfileLine>
            </styles.AssistantMessage>
          </styles.AssistantTurn>
        )}

        {!hasReply && !isReplying && (
          <styles.AssistantTurn $continued={showProfileLine}>
            <AvatarColumn continued={showProfileLine} thinking={!error} />
            <styles.AssistantMessage>
              {error ? (
                <>
                  <styles.EmptyHeadline>{greetingHeadline}</styles.EmptyHeadline>
                  <styles.EmptyBody>{greetingBody}</styles.EmptyBody>
                  <styles.Chips>
                    {starterPrompts(snapshot).map(prompt => (
                      <styles.Chip key={prompt} type="button" onClick={() => send(prompt, { source: "starter" })}>
                        {prompt}
                      </styles.Chip>
                    ))}
                  </styles.Chips>
                </>
              ) : (
                <Thinking label={isLoadingPrevious ? "Catching up…" : "Looking at the post…"} />
              )}
            </styles.AssistantMessage>
          </styles.AssistantTurn>
        )}

        {history.map((message, index) => {
          if (hiddenTurns.has(index)) return null;
          const animate = index >= initialHistoryLength.current;
          if (message.role === "user") return <UserTurn key={index} message={message} animate={animate} />;
          if (message.role === "assistant") {
            const isLatestReply = index === lastVisibleIndex && !isReplying;
            return (
              <AssistantTurn
                key={index}
                message={message}
                results={results}
                hiddenCallIds={recoveredFailures}
                channels={channels}
                profiles={profiles}
                continued={rows.continued.has(index)}
                working={isWorking && rows.trailingHead === index}
                animate={animate}
                onPickOption={isLatestReply ? (option, pick) => send(option, { source: "option", ...pick }) : undefined}
              />
            );
          }
          return null;
        })}

        {isReplying && (
          <StreamingTurn text={streamedText} threadRef={threadRef} revealRef={revealRef} holdBack={isQuietCheck} continued={rows.trailingHead !== null} />
        )}
        {!!error && <styles.ErrorText>{error}</styles.ErrorText>}
      </styles.Thread>

      {isDragging && <styles.DropHint>Drop files to add them to the chat</styles.DropHint>}

      <styles.Footer>
        {uploads.length > 0 && (
          <styles.UploadList>
            {uploads.map(upload => (
              <styles.UploadChip key={upload.id} $status={upload.status}>
                <span>
                  {upload.status === "uploading" && `Uploading ${upload.name}…`}
                  {upload.status === "ready" && upload.name}
                  {upload.status === "failed" && `${upload.name}: ${upload.error}`}
                </span>
                <styles.RemoveUpload type="button" onClick={() => removeUpload(upload.id)} aria-label={`Remove ${upload.name}`}>
                  ×
                </styles.RemoveUpload>
              </styles.UploadChip>
            ))}
          </styles.UploadList>
        )}
        <styles.InputBox onSubmit={onSubmit}>
          <styles.Input
            ref={inputRef}
            rows={1}
            value={draft}
            placeholder="Tell Create AI what to write or change"
            onChange={event => {
              setDraft(event.target.value);
              fitTextarea(event.target);
            }}
            onKeyDown={onKeyDown}
          />
          <styles.InputActions>
            <styles.AttachButton type="button" aria-label="Attach a file" onClick={() => fileInputRef.current?.click()}>
              <Plus size={18} />
            </styles.AttachButton>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILES}
              hidden
              onChange={event => {
                if (event.target.files) addFiles(event.target.files);
                // Reset the input value so re-selecting the same file still fires onChange.
                event.target.value = "";
              }}
            />
            <ProfileMenu
              variant="compact"
              opensUp
              profiles={profiles}
              activeId={profileId}
              onPick={pickProfile}
              disabled={isReplying}
              touchedAt={profileTouchedAt}
            />
            <styles.SendButton type="submit" disabled={!canSend} aria-label="Send">
              <ArrowUp size={16} />
            </styles.SendButton>
          </styles.InputActions>
        </styles.InputBox>
      </styles.Footer>
    </styles.Chat>
  );
};

export default CreateAI;
